import { create } from 'zustand';

import { listObjectDefinitions } from '../../content/registry/objectRegistry';

interface ThumbnailState {
  /** Data URLs keyed by assetId. Missing entries mean not yet rendered. */
  thumbnails: Record<string, string>;
  /** AssetIds still queued for rendering, head-first. */
  queue: string[];
  /** True once every registry entry has been processed (or overridden). */
  ready: boolean;

  /**
   * Seed the queue with every registry asset that is not already cached.
   * Hand-authored overrides are absorbed straight into the cache.
   */
  initialise: () => void;
  /** Pop the next assetId to render, or null when queue is empty. */
  nextAsset: () => string | null;
  /** Commit a rendered thumbnail and advance state. */
  setThumbnail: (assetId: string, dataUrl: string) => void;
  /** Mark an asset as processed without a thumbnail (renderer reported an error). */
  markSkipped: (assetId: string) => void;
  /**
   * Drop a cached thumbnail (e.g. an override URL that 404s) and queue the
   * asset for live generation instead. Lazy by construction: only a tile
   * that actually rendered its broken image calls this.
   */
  requeueForRender: (assetId: string) => void;
  /** Wipe cache - used when registry definitions change during dev HMR. */
  reset: () => void;
}

export const useThumbnailStore = create<ThumbnailState>((set, get) => ({
  thumbnails: {},
  queue: [],
  ready: false,

  initialise: () => {
    const state = get();
    const initialThumbnails: Record<string, string> = { ...state.thumbnails };
    const requeued = new Set(state.queue);
    const queue: string[] = [...state.queue];

    for (const definition of listObjectDefinitions()) {
      // A queued asset is being live-generated (possibly because its
      // override 404'd) - don't re-absorb the override over it.
      if (requeued.has(definition.id)) continue;
      if (definition.editor.thumbnailOverride) {
        initialThumbnails[definition.id] = definition.editor.thumbnailOverride;
        continue;
      }
      if (initialThumbnails[definition.id]) {
        continue;
      }
      queue.push(definition.id);
    }

    set({
      thumbnails: initialThumbnails,
      queue,
      ready: queue.length === 0,
    });
  },

  nextAsset: () => {
    const { queue } = get();
    return queue[0] ?? null;
  },

  setThumbnail: (assetId, dataUrl) => {
    set((state) => {
      const queue = state.queue.filter((id) => id !== assetId);
      return {
        thumbnails: { ...state.thumbnails, [assetId]: dataUrl },
        queue,
        ready: queue.length === 0,
      };
    });
  },

  markSkipped: (assetId) => {
    set((state) => {
      const queue = state.queue.filter((id) => id !== assetId);
      return {
        queue,
        ready: queue.length === 0,
      };
    });
  },

  requeueForRender: (assetId) => {
    set((state) => {
      if (state.queue.includes(assetId)) return state;
      const thumbnails = { ...state.thumbnails };
      delete thumbnails[assetId];
      return { thumbnails, queue: [...state.queue, assetId], ready: false };
    });
  },

  reset: () => set({ thumbnails: {}, queue: [], ready: false }),
}));
